import { PEPTIDE_CATEGORIES, PEPTIDE_BADGES, BRAND } from "@/data/content";
import {
  FlaskConical,
  ShieldCheck,
  Package,
  BadgeCheck,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";

const BADGE_ICONS = { ShieldCheck, FlaskConical, Package, BadgeCheck };

export default function Peptides({ onInquire }) {
  return (
    <section
      id="peptides"
      data-testid="peptides-section"
      className="relative py-24 md:py-36 overflow-hidden border-t border-ng-line bg-ng-bg hex-bg"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="label-eyebrow mb-5">03 · NextGen Pro Peptides</div>
          <h2 className="font-display font-bold uppercase text-5xl md:text-7xl tracking-tight leading-[0.95]">
            <span className="electric-text">PEPTIDES</span>
          </h2>
          <p className="mt-4 font-display tracking-[0.28em] uppercase text-ng-blue text-sm font-semibold">
            Research-Grade Compounds &nbsp;·&nbsp; High-Purity
          </p>
          <p className="mt-6 text-white/65 font-light max-w-2xl mx-auto leading-relaxed">
            Premium-sourced, third-party tested research compounds for
            laboratory research. Available exclusively through {BRAND.peptide_brand}.
          </p>
        </div>

        {/* Trust badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-16">
          {PEPTIDE_BADGES.map((b) => {
            const Icon = BADGE_ICONS[b.icon] || ShieldCheck;
            return (
              <div
                key={b.title}
                data-testid={`peptide-badge-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="border border-ng-line bg-ng-surface/60 backdrop-blur-md p-4 flex items-center gap-3 hover:border-ng-blue/50 transition-colors"
              >
                <div className="w-10 h-10 border border-ng-blue/40 flex items-center justify-center text-ng-blue flex-shrink-0">
                  <Icon size={18} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-display font-bold uppercase text-xs text-white tracking-wider">
                    {b.title}
                  </div>
                  <div className="text-[10px] text-ng-blue uppercase tracking-widest mt-0.5">
                    {b.sub}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Categories */}
        <div className="space-y-12">
          {PEPTIDE_CATEGORIES.map((cat) => (
            <CategoryBlock key={cat.id} category={cat} onInquire={onInquire} />
          ))}
        </div>

        {/* Compliance banner */}
        <div className="mt-16 border border-yellow-500/40 bg-yellow-500/5 p-5 md:p-6 flex flex-col md:flex-row gap-4 items-start md:items-center">
          <AlertTriangle
            size={28}
            strokeWidth={1.5}
            className="text-yellow-400 flex-shrink-0"
          />
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs md:text-sm">
            <span className="font-display font-bold uppercase tracking-widest text-yellow-400">
              For Research Use Only
            </span>
            <span className="text-white/70 uppercase tracking-wider font-display">
              Not For Human or Animal Consumption
            </span>
            <span className="text-white/70 uppercase tracking-wider font-display">
              Not Intended For Medical or Therapeutic Use
            </span>
          </div>
        </div>

        <p className="mt-6 text-white/45 text-xs max-w-3xl mx-auto text-center font-light leading-relaxed">
          Consult a licensed medical professional before starting any peptide
          therapy. All compounds offered by NextGen Pro Peptides are sold strictly
          for laboratory research purposes.{" "}
          <a href="/medical-disclaimer" data-testid="peptides-medical-link" className="text-ng-blue hover:underline">Read full Medical Disclaimer</a>.
        </p>

        {/* Text J CTA */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col items-center gap-3 border border-ng-line bg-ng-surface px-8 py-6">
            <div className="label-eyebrow">Inquire About Compounds</div>
            <a
              href={`sms:${BRAND.phone_link}`}
              data-testid="peptides-text-j"
              className="font-display font-bold text-2xl md:text-3xl tracking-tight"
            >
              <span className="chrome-text">Text or Call </span>
              <span className="electric-text">Coach J · {BRAND.phone}</span>
            </a>
            <div className="text-white/50 text-xs uppercase tracking-[0.25em] font-display">
              {BRAND.city} · {BRAND.region}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryBlock({ category, onInquire }) {
  return (
    <div data-testid={`peptide-category-${category.id}`}>
      <div className="flex items-end justify-between border-b border-ng-line pb-4 mb-6 gap-4 flex-wrap">
        <div>
          <h3 className="font-display font-bold uppercase text-2xl md:text-3xl text-white tracking-tight">
            {category.title}
          </h3>
          <p className="label-muted mt-2">{category.short}</p>
        </div>
        <span className="font-display font-bold text-ng-blue text-2xl">
          {String(category.items.length).padStart(2, "0")}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {category.items.map((p) => (
          <PeptideCard key={p.name} peptide={p} onClick={() => onInquire(p.name)} />
        ))}
      </div>
    </div>
  );
}

function PeptideCard({ peptide, onClick }) {
  const slug = peptide.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/g, "");
  return (
    <div
      data-testid={`peptide-card-${slug}`}
      className="group relative bg-ng-surface backdrop-blur-xl border border-ng-line p-6 hover:border-ng-blue/50 hover:shadow-[0_0_30px_-10px_rgba(30,144,255,0.5)] transition-all duration-500 flex flex-col"
    >
      <div className="flex items-start justify-between mb-3">
        <span className="w-2 h-2 rounded-full bg-ng-blue electric-pulse" />
        <FlaskConical size={16} strokeWidth={1.5} className="text-ng-blue/60 group-hover:text-ng-blue transition-colors" />
      </div>
      <h4 className="font-display font-bold uppercase text-lg md:text-xl text-white tracking-tight">
        {peptide.name}
      </h4>
      <p className="mt-3 text-white/65 text-sm font-light leading-relaxed flex-1">
        {peptide.blurb}
      </p>
      <button
        data-testid={`peptide-inquire-${slug}`}
        onClick={onClick}
        className="mt-5 inline-flex items-center justify-between border border-ng-line text-white text-[11px] uppercase tracking-[0.22em] font-bold font-display py-2.5 px-3 hover:border-ng-blue hover:text-ng-blue hover:bg-ng-blue/5 transition-colors"
      >
        Inquire
        <ArrowRight size={12} strokeWidth={2} />
      </button>
    </div>
  );
}
